// BaseCustomRoleService.js

import {
  collection,
  doc,
  onSnapshot,
  query,
} from 'firebase/firestore';
import { collections, db, functions, httpsCallable } from 'firebaseConfig';
import { defaultCustomRolePermissions } from 'security/rbac';
import { buildTenantCallableFields } from 'utilities/basePersistence';

const callable = (name) => httpsCallable(functions, name);

function rolesCollection(baseKey) {
  return collection(db, collections.bases, baseKey, 'roles');
}

function roleDocument(baseKey, roleId) {
  return doc(db, collections.bases, baseKey, 'roles', roleId);
}

export function subscribeToBaseCustomRoles(baseKey, callback, onError) {
  const normalizedBaseKey = String(baseKey || '').trim();
  if (!normalizedBaseKey || typeof callback !== 'function') {
    return () => {};
  }

  return onSnapshot(
    query(rolesCollection(normalizedBaseKey)),
    (snapshot) => {
      const rows = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        key: docSnap.id,
        ...(docSnap.data() || {}),
      }));
      rows.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
      callback(rows);
    },
    (error) => {
      if (typeof onError === 'function') onError(error);
      else console.error('[BaseCustomRoleService] subscribeToBaseCustomRoles error:', error);
      callback([]);
    }
  );
}

export function subscribeToBaseCustomRole(baseKey, roleId, callback, onError) {
  const normalizedBaseKey = String(baseKey || '').trim();
  const normalizedRoleId = String(roleId || '').trim();
  if (!normalizedBaseKey || !normalizedRoleId || typeof callback !== 'function') {
    return () => {};
  }

  return onSnapshot(
    roleDocument(normalizedBaseKey, normalizedRoleId),
    (snapshot) => {
      if (!snapshot.exists()) {
        callback(null);
        return;
      }
      callback({
        id: snapshot.id,
        key: snapshot.id,
        ...(snapshot.data() || {}),
      });
    },
    (error) => {
      if (typeof onError === 'function') onError(error);
      else console.error('[BaseCustomRoleService] subscribeToBaseCustomRole error:', error);
      callback(null);
    }
  );
}

export async function createBaseRole(baseKey, {
  name,
  description,
  permissions,
  typeAccess,
} = {}) {
  const fn = callable('createBaseRole');
  const result = await fn({
    ...buildTenantCallableFields(baseKey),
    name,
    description,
    permissions: permissions || defaultCustomRolePermissions(),
    typeAccess,
  });
  return result?.data || null;
}

export async function updateBaseRole(baseKey, roleId, patch = {}) {
  const fn = callable('updateBaseRole');
  const result = await fn({
    ...buildTenantCallableFields(baseKey),
    roleId,
    ...patch,
  });
  return result?.data || null;
}

export async function deleteBaseRole(baseKey, roleId) {
  const fn = callable('deleteBaseRole');
  const result = await fn({
    ...buildTenantCallableFields(baseKey),
    roleId,
  });
  return result?.data || null;
}
